import { Injectable } from '@angular/core';
import { BehaviorSubject, from, Observable } from 'rxjs';
import { EncryptionInterface } from '../interfaces/encryption.interface';
import { compressToUint8Array, decompressFromUint8Array } from 'lz-string';
import { ENCRYPTION_ALGORITHM, PREFIX_ENCRYPTED_CONTENT } from '../encryption.const';
import { uint8ToBase64 } from '../encryption.utils';

@Injectable({
    providedIn: 'root',
})
export class EncryptionService implements EncryptionInterface {
    private encryptionKey: CryptoKey | null = null;
    private readonly log = console;
    private readonly initialized = new BehaviorSubject<boolean>(false);

    public init(encryptionKey: string): Observable<void> {
        if (encryptionKey.length === 0) {
            throw new Error(`EncryptionService: init: No encryption keys found for the user`);
        }
        return from(this.setEncryptionKey(encryptionKey));
    }

    public get isInitialized(): boolean {
        return this.initialized.getValue();
    }

    public get selectInitialized(): Observable<boolean> {
        return this.initialized.asObservable();
    }

    private async setEncryptionKey(keyString: string): Promise<void> {
        try {
            const keyLength = keyString.length;
            const key = new Uint8Array(new ArrayBuffer(keyLength));

            for (let i = 0; i < keyLength; i++) {
                key[i] = keyString.charCodeAt(i);
            }

            this.encryptionKey = await crypto.subtle.importKey('raw', key, { name: ENCRYPTION_ALGORITHM }, false, [
                'encrypt',
                'decrypt',
            ]);
            this.initialized.next(true);
        } catch (error) {
            this.log.error(`EncryptionService: setEncryptionKey: Failed to import key`, error);
            throw new Error(`EncryptionService: setEncryptionKey: Failed to import key`);
        }
    }

    private async encryptRaw(data: string): Promise<string> {
        if (!this.encryptionKey) {
            throw new Error(`EncryptionService: encryptRaw: Encryption key is not initialized`);
        }

        const iv = crypto.getRandomValues(new Uint8Array(16));
        const encodedData = new TextEncoder().encode(data);
        const encryptedBuffer = await crypto.subtle.encrypt(
            { name: ENCRYPTION_ALGORITHM, iv },
            this.encryptionKey,
            encodedData,
        );

        const combined = new Uint8Array(iv.length + encryptedBuffer.byteLength);
        combined.set(iv);
        combined.set(new Uint8Array(encryptedBuffer), iv.length);

        return PREFIX_ENCRYPTED_CONTENT + uint8ToBase64(combined);
    }

    public encryptAsync(data: string): Promise<string> {
        return this.encryptRaw(data);
    }

    public async encryptAndCompressAsync(data: string): Promise<Uint8Array> {
        const encrypted = await this.encryptRaw(data);
        return compressToUint8Array(encrypted);
    }

    private async decryptRaw(encryptedBase64: string): Promise<string> {
        if (!this.encryptionKey) {
            throw new Error(`EncryptionService: decryptRaw: Encryption key is not initialized`);
        }

        if (!encryptedBase64.startsWith(PREFIX_ENCRYPTED_CONTENT)) {
            return encryptedBase64;
        }

        const base64Data = encryptedBase64.substring(PREFIX_ENCRYPTED_CONTENT.length);
        const combined = Uint8Array.from(atob(base64Data), (c) => c.charCodeAt(0));
        const iv = combined.slice(0, 16);
        const data = combined.slice(16);

        const decryptedBuffer = await crypto.subtle.decrypt(
            { name: ENCRYPTION_ALGORITHM, iv },
            this.encryptionKey,
            data,
        );

        return new TextDecoder().decode(decryptedBuffer);
    }

    public decryptAsync(data: string): Promise<string> {
        return this.decryptRaw(data);
    }

    public async decryptAndDecompressAsync(data: Uint8Array): Promise<string> {
        const decompressed = decompressFromUint8Array(data);
        if (!decompressed) {
            throw new Error(`EncryptionService: decompressFromUint8Array: Unable to decompress data!`);
        }
        return this.decryptRaw(decompressed);
    }
}
